'use client';
import clsx from 'clsx';
import Link from 'next/link';
import { useEffect, useState } from 'react';

import { extractHeadings } from '@/utils';

export default function TableOfContents({ blocks }) {
  const headings = extractHeadings(blocks);
  const [activeId, setActiveId] = useState('');

  // Enhanced scrollspy effect with IntersectionObserver
  useEffect(() => {
    if (typeof window === 'undefined' || !headings.length) return;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      {
        rootMargin: '-100px 0px -80% 0px',
        threshold: 0,
      },
    );

    // Observe all heading elements
    headings.forEach(heading => {
      const element = document.getElementById(heading.slug);
      if (element) observer.observe(element);
    });

    return () => {
      headings.forEach(heading => {
        const element = document.getElementById(heading.slug);
        if (element) observer.unobserve(element);
      });
    };
  }, [headings]);

  // Scroll to hash on initial load
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const id = hash.substring(1);
      const element = document.getElementById(id);
      if (element) {
        const yOffset = -120;
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
        setActiveId(id);
      }
    }
  }, []);

  if (headings.length === 0) return null;

  return (
    <div className="sticky top-24 hidden h-[calc(100vh-120px)] overflow-y-auto pr-4 pb-10 lg:block">
      <div className="rounded-xl border border-gray-200 bg-white/90 p-6 shadow-lg backdrop-blur-sm dark:border-gray-700 dark:bg-gray-900/90">
        <h3 className="mb-5 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text pb-3 text-xl font-bold text-transparent dark:border-gray-700">
          Table of Contents
        </h3>
        <nav className="toc-nav">
          <ul className="space-y-3">
            {headings.map(heading => (
              <li
                key={heading.slug}
                className={clsx({
                  'ml-0': heading.level === 2,
                  'ml-2': heading.level === 3,
                  'ml-4': heading.level === 4,
                })}
              >
                <Link
                  prefetch={false}
                  href={`#${heading.slug}`}
                  onClick={e => {
                    e.preventDefault();
                    const element = document.getElementById(heading.slug);
                    if (element) {
                      const yOffset = -120;
                      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                      window.scrollTo({ top: y, behavior: 'smooth' });
                      window.history.pushState(null, '', `#${heading.slug}`);
                      setActiveId(heading.slug);
                    }
                  }}
                  className={clsx(
                    'block transition-all duration-200 ease-in-out',
                    'border-l-3 py-2 pl-3',
                    {
                      'border-blue-600 font-semibold text-blue-600 dark:border-blue-400 dark:text-blue-400':
                        activeId === heading.slug,
                      'border-transparent text-gray-600 hover:border-blue-300 hover:text-blue-600 dark:text-gray-400 dark:hover:border-blue-600 dark:hover:text-blue-400':
                        activeId !== heading.slug,
                    },
                    'transform hover:translate-x-1',
                  )}
                >
                  <span className="text-sm md:text-base">{heading.text}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </div>
  );
}
